/**
 * SEO-Einstiegsseiten — Zugriff auf die Registry.
 *
 * `landing-pages.json` ist ein ZEICHENGLEICHER Spiegel von
 * `backend/app/services/seo/landing_pages.json` (Pytest
 * `test_seo_landing.py` erzwingt das). Dadurch benutzen Routen,
 * Metadaten, Sitemap und Trefferzahl-Gate genau dieselben Definitionen.
 */
import registry from "./landing-pages.json";
import indexability from "./indexability";

// Gemeinsame Indexierungsregel — identisch im Build-Skript
// (`scripts/prerender-seo.js`) und zur Laufzeit.
export const seoIndexState = indexability.seoIndexState;
export const seoRobots = indexability.seoRobots;
export const SEO_STATE_THIN = indexability.STATE_THIN;
export const SEO_STATE_UNKNOWN = indexability.STATE_UNKNOWN;

export const SEO_HOST = registry.canonical_host;
export const SEO_X_DEFAULT = registry.x_default_lang;
export const SEO_DEFAULT_MIN_RESULTS = registry.default_min_results;
export const SEO_PAGES = registry.pages;

const byUrl = new Map();
const byKey = new Map();
SEO_PAGES.forEach((p) => {
  byKey.set(p.key, p);
  Object.entries(p.i18n).forEach(([lang, loc]) => {
    byUrl.set(`${lang}/${loc.slug}`, p);
  });
});

export const SEO_LANGS = Array.from(
  new Set(SEO_PAGES.flatMap((p) => Object.keys(p.i18n))),
);

export function findSeoPage(lang, slug) {
  if (!lang || !slug) return null;
  return byUrl.get(`${lang}/${String(slug).toLowerCase()}`) || null;
}

export function seoPageByKey(key) {
  return byKey.get(key) || null;
}

export function seoPath(lang, page) {
  const loc = page?.i18n?.[lang];
  return loc ? `/${lang}/${loc.slug}` : null;
}

/** Echte sprachliche Entsprechungen DIESER Seite — nie geraten. */
export function seoAlternates(page) {
  return Object.keys(page.i18n).map((lang) => ({
    lang,
    href: `${SEO_HOST}${seoPath(lang, page)}`,
  }));
}

export function seoMinResults(page) {
  return Number(page.min_results || SEO_DEFAULT_MIN_RESULTS);
}

/** Filtersatz für `/api/public/discovery/feed/{vertical}`. */
export function seoFeedParams(page, overrides = {}) {
  const out = { quality: "all", sort: "newest", ...page.filters, ...overrides };
  Object.keys(out).forEach((k) => {
    if (out[k] === null || out[k] === undefined || out[k] === "") delete out[k];
  });
  return out;
}

/** Verwandte Seiten — nur echte Fassungen DERSELBEN Sprache. Ein
 *  Verweis auf eine andere Sprache würde den internen Linkgraphen
 *  vermischen (und hreflang widersprechen). */
export function relatedSeoPages(page, lang) {
  return (page.related || [])
    .map((key) => seoPageByKey(key))
    .filter((p) => p && p.i18n[lang])
    .map((p) => ({ page: p, lang, path: seoPath(lang, p),
                   label: p.i18n[lang].h1 }));
}

/** Kuratierte Startpunkte für Home/Footer — je Sprache. */
export function popularSeoPages(lang, limit = 8) {
  return SEO_PAGES.filter((p) => p.i18n[lang])
    .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0))
    .slice(0, limit)
    .map((p) => ({ page: p, lang, path: seoPath(lang, p),
                   label: p.i18n[lang].h1 }));
}

/** Filter → Alert-Vokabular (`/api/saved-searches`). `dropped` nennt
 *  ehrlich, was ein Alarm NICHT abbilden kann. */
export function seoAlertPayload(page, lang) {
  const f = page.filters || {};
  const filters = {};
  const dropped = [];
  if (f.country) filters.country = f.country;
  if (f.city) filters.region = f.city;
  if (f.transaction_type) filters.transaction_type = f.transaction_type;
  if (f.object_type) filters.object_types = [f.object_type];
  if (f.yield_min) filters.yield_min = f.yield_min;
  if (f.quality === "deals") dropped.push("quality");
  return {
    vertical: page.vertical,
    name: (page.i18n[lang] || Object.values(page.i18n)[0]).h1.slice(0, 80),
    filters,
    dropped,
  };
}
